import { Link } from "react-router-dom";
import {
  Scale, FileSearch, BookUser, Stamp, Calculator, ShieldCheck, Home as HomeIcon, Flag,
  ArrowRight, Route, MessagesSquare, FolderLock, HeartHandshake, Compass, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import CTASection from "../components/CTASection";
import { JamaicaFlag, BahamasFlag } from "../components/Flags";
import { SERVICE_CARDS, JOURNEY_STEPS, WHY_FULL_CIRCLE, FAQS_HOME, IMAGES } from "../data/content";

const ICONS = { Scale, FileSearch, BookUser, Stamp, Calculator, ShieldCheck, Home: HomeIcon, Flag, Route, MessagesSquare, FolderLock, HeartHandshake, Compass };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-ivory via-sage-light/30 to-ivory py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 animate-fade-up">
              <div className="inline-flex items-center gap-2.5 rounded-full border border-teal/30 bg-teal-light/40 px-4 py-2 mb-4" data-testid="caribbean-badge">
                <BahamasFlag className="h-4 w-6 rounded-[3px] ring-1 ring-white/20" />
                <JamaicaFlag className="h-4 w-6 rounded-[3px] ring-1 ring-white/20" />
                <span className="text-xs font-semibold text-teal tracking-wide">Caribbean immigration specialists — The Bahamas · Jamaica</span>
              </div>
              <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-5">One Place. Every Step. Full Circle.</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-navy leading-[1.12] text-balance">
                Immigration Is Only One Part of Starting Over.
              </h1>
              <p className="mt-6 text-lg sm:text-xl font-sans text-mutedink leading-relaxed max-w-xl">
                CF Full Circle helps immigrants and families navigate authorized immigration legal services, essential documents, identification, tax needs, and the practical steps of building a new life in the United States — through one organized journey.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-8 h-13 py-4 text-base font-semibold" data-testid="hero-start-journey-button">
                  <Link to="/start">Start Your Full Circle Journey <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-teal/40 text-teal hover:bg-teal hover:text-white rounded-full px-8 h-13 py-4 text-base" data-testid="hero-explore-services-button">
                  <Link to="/immigration">Explore Our Services</Link>
                </Button>
              </div>
              <Link to="/login" className="mt-5 inline-block text-sm font-medium text-teal hover:text-teal-dark transition-colors" data-testid="hero-existing-client-link">
                Existing client? Sign in
              </Link>
            </div>
            <div className="lg:col-span-6 relative animate-fade-up stagger-2">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-champagne-light border border-champagne/40" aria-hidden="true" />
              <div className="absolute -bottom-8 -left-8 w-56 h-56 rounded-full bg-sage-light" aria-hidden="true" />
              <img src={IMAGES.hero} alt="A family walking together toward a new beginning" className="relative rounded-[2rem] w-full h-[420px] lg:h-[520px] object-cover shadow-xl" />
              <div className="absolute bottom-6 left-6 right-6 sm:right-auto bg-surface/95 backdrop-blur rounded-2xl p-5 shadow-lg max-w-xs" data-testid="hero-journey-card">
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-teal mb-2">Your Full Circle Journey</p>
                <div className="space-y-2">
                  {["Intake complete", "Journey plan ready", "Documents organized"].map((t, i) => (
                    <div key={t} className="flex items-center gap-2 text-sm text-ink">
                      <CheckCircle2 className={`h-4 w-4 ${i < 2 ? "text-sage" : "text-line"}`} /> {t}
                    </div>
                  ))}
                </div>
                <div className="mt-3 h-2 rounded-full bg-sand overflow-hidden">
                  <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-teal to-sage" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT DO YOU NEED HELP WITH */}
      <section className="py-16 lg:py-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">Where would you like to start?</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-navy tracking-tight">What do you need help with?</h2>
            <p className="mt-4 text-lg text-mutedink">Choose the area that fits your situation. Not sure? Start anywhere — we organize the rest.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICE_CARDS.map((card, idx) => {
              const Icon = ICONS[card.icon];
              return (
                <Link
                  key={card.id}
                  to={card.href}
                  data-testid={`service-card-${card.id}`}
                  className="bg-surface border border-line hover:border-teal rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 group flex flex-col animate-fade-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="h-11 w-11 rounded-xl bg-teal-light flex items-center justify-center mb-5 group-hover:bg-teal transition-colors">
                    <Icon className="h-5 w-5 text-teal group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-sans font-semibold text-navy">{card.title}</h3>
                  <p className="mt-2 text-sm text-mutedink leading-relaxed flex-1">{card.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-teal group-hover:gap-2 transition-all">
                    {card.cta} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FULL CIRCLE JOURNEY */}
      <section className="py-16 lg:py-24 bg-sand/60" data-testid="journey-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">How it works</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-navy tracking-tight">The Full Circle Journey</h2>
            <p className="mt-4 text-lg text-mutedink">Nine clear steps. You always know where you are and what comes next.</p>
          </div>
          <div className="relative">
            <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal via-sage to-champagne sm:-translate-x-1/2" aria-hidden="true" />
            <div className="space-y-10">
              {JOURNEY_STEPS.map((step, i) => (
                <div key={step.title} className={`relative flex gap-6 sm:gap-0 ${i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"} items-start`} data-testid={`journey-step-${i + 1}`}>
                  <div className="relative z-10 flex-shrink-0 sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                    <div className="h-12 w-12 rounded-full bg-surface border-2 border-teal flex items-center justify-center font-serif font-semibold text-teal shadow-sm">
                      {i + 1}
                    </div>
                  </div>
                  <div className={`sm:w-1/2 ${i % 2 === 0 ? "sm:pr-16" : "sm:pl-16"} bg-surface border border-line rounded-xl p-5 shadow-sm`}>
                    <h3 className="text-base font-sans font-semibold text-navy">{step.title}</h3>
                    <p className="mt-1 text-sm text-mutedink leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-16 text-center">
            <p className="font-serif text-xl sm:text-2xl text-navy italic">One client. One organized journey. One place to understand what comes next.</p>
            <Button asChild size="lg" className="mt-6 bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-8" data-testid="journey-start-button">
              <Link to="/start">Start My Journey <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHY FULL CIRCLE */}
      <section className="py-16 lg:py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">Why Full Circle</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-navy tracking-tight">You do not have to figure everything out alone.</h2>
              <p className="mt-4 text-lg text-mutedink leading-relaxed">We help organize your journey — clearly, honestly, and within the boundaries of what we are authorized to do.</p>
              <img src={IMAGES.counseling} alt="A welcoming consultation at CF Full Circle" className="mt-8 rounded-2xl w-full h-56 object-cover shadow-md hidden lg:block" />
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {WHY_FULL_CIRCLE.map((item, i) => {
                const Icon = ICONS[item.icon];
                return (
                  <div key={item.title} className={`bg-surface border border-line rounded-xl p-6 shadow-sm hover:shadow-md hover:border-sage transition-all ${i === 4 ? "sm:col-span-2" : ""}`} data-testid={`why-card-${i}`}>
                    <div className="h-11 w-11 rounded-xl bg-sage-light flex items-center justify-center mb-4">
                      <Icon className="h-5 w-5 text-sage" />
                    </div>
                    <h3 className="text-lg font-sans font-semibold text-navy">{item.title}</h3>
                    <p className="mt-2 text-sm text-mutedink leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-sand/60" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">Common questions</p>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">Honest answers, in plain English</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS_HOME.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-surface border border-line rounded-xl px-6" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-sans font-semibold text-navy hover:text-teal py-5">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-mutedink leading-relaxed pb-5">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
